import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, ArrowLeft, Calendar, User, Clock, CheckCircle, 
  AlertCircle, Share2, Download, MessageSquare, Baby,
  Smile, Activity, BookOpen, Target
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';
import DaysInsightsReport from '../components/DaysInsightsReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverReportDetail = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    fetchReport();
  }, [navigate, reportId]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      const response = await axios.get(`${BACKEND_URL}/api/observer/report/${reportId}?token=${token}`);
      setReport(response.data.report);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast({ title: "Error", description: "Failed to load report", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/shared/report/${reportId}`;
    navigator.clipboard.writeText(shareUrl);
    toast({ title: "Link Copied", description: "Report link copied to clipboard" });
  };

  const handleDownload = () => {
    const token = localStorage.getItem('observer_token');
    window.open(`${BACKEND_URL}/api/observer/report/${reportId}/download?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 text-center">
          <p className="text-gray-500">Report not found</p>
          <Button onClick={() => navigate('/observer/reports')} className="mt-4">
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="observer-report-detail">
      <ObserverNav />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/observer/reports')} data-testid="back-to-reports-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Session Report</h1>
              <p className="text-gray-600">{report.report_date || report.generated_at?.slice(0, 10)}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleShare} data-testid="share-report-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
            <Button variant="outline" onClick={handleDownload} data-testid="download-report-btn">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>

        {/* AI-GENERATED DAY'S INSIGHTS (call recording reports) */}
        {report.report_data ? (
          <>
            <DaysInsightsReport data={report.report_data} />

            {/* Transcript collapsible */}
            {report.transcript && (
              <Card className="mt-6" data-testid="call-transcript-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" /> Full Transcript
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">
                      {report.transcript}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
        {/* Report Info Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Baby className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Child</p>
                  <p className="font-semibold">{report.child_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Observer</p>
                  <p className="font-semibold">{report.observer_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-semibold">{report.report_date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  report.review_status === 'approved' ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  <CheckCircle className={`w-5 h-5 ${
                    report.review_status === 'approved' ? 'text-green-600' : 'text-amber-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-semibold capitalize">{report.review_status?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mood & Engagement */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Smile className="w-4 h-4 text-amber-500" /> Child Mood
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                report.child_mood === 'happy' ? 'bg-green-100 text-green-700' :
                report.child_mood === 'calm' ? 'bg-blue-100 text-blue-700' :
                report.child_mood === 'engaged' ? 'bg-purple-100 text-purple-700' :
                report.child_mood === 'curious' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {report.child_mood || 'Not recorded'}
              </span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-green-500" /> Engagement Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                report.engagement_level === 'high' ? 'bg-green-100 text-green-700' :
                report.engagement_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {report.engagement_level || 'Not recorded'}
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Session Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-600" /> Session Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">
              {report.session_summary || report.report_content || report.observation || 'No summary available'}
            </p>
          </CardContent>
        </Card>

        {/* Full Transcript */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" /> Full Transcript
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-gray-700 whitespace-pre-wrap text-sm">
                {report.transcript || report.observation || 'No transcript available'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Topics Discussed */}
        {report.topics_discussed && report.topics_discussed.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" /> Topics Discussed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {report.topics_discussed.map((topic, idx) => (
                  <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                    {topic}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Observations */}
        {report.key_observations && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" /> Key Observations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{report.key_observations}</p>
            </CardContent>
          </Card>
        )}

        {/* Principal Feedback (observer-facing) */}
        {(report.principal_feedback_for_observer || report.principal_feedback) && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <User className="w-5 h-5" /> Principal Feedback
                {report.review_status === 'rejected' && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 ml-2">
                    Rejected
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-blue-700 whitespace-pre-wrap">
                {report.principal_feedback_for_observer || report.principal_feedback}
              </p>
            </CardContent>
          </Card>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default ObserverReportDetail;
